import React, { ChangeEvent, useCallback, useMemo } from 'react'
import { LoadFormState, LoadState, LoadTypeOption } from '../../hooks/useLoadState'
import { ImposedLoadCategory } from '../../hooks/useMaterialState'
import { CrossSection, LoadCategory, SMBeamLoad, SMBeamModel } from '../../types'
import AppliedLoadsList from './AppliedLoadsList'
import LoadInputForm from './LoadInputForm'
import PTCableConfigPanel from './PTCableConfigPanel'
import { ALL_SPANS_ID } from '../../handlers'
import { useTranslation } from '@structura/i18n'

interface PrestressingMaterialProps {
  fpk: number
  fp01k: number
  elasticModulus: number
  relaxationClass: number
  gammaS: number
}

interface ConcreteMaterialProps {
  fck: number
  elasticModulus: number
  relativeHumidity: number
  notionalSize: number
  loadingAge: number
  cementType: 'S' | 'N' | 'R'
}

interface CrossSectionProps {
  area: number
  inertia: number
  heightMm?: number  // Section height in mm for PT eccentricity calculation
}

interface LoadConfigurationPanelProps
{
  model: SMBeamModel
  setModel: React.Dispatch<React.SetStateAction<SMBeamModel>>
  selectedSpan: number
  setSelectedSpan: (spanId: number) => void
  selectedLoadType: LoadTypeOption
  setSelectedLoadType: (type: LoadTypeOption) => void
  loadForm: LoadFormState
  setLoadForm: LoadState['setLoadForm']
  addLoadToSpan: () => void
  removeLoad: (loadId: string, spanId: number) => void
  editLoad: (loadId: string, spanId: number) => void
  editingLoadId: string | null
  setEditingLoadId: (id: string | null) => void
  calculateSelfWeight: (crossSection: CrossSection) => number
  currentMaterial: string
  // For PT cable loss calculations
  prestressingMaterial?: PrestressingMaterialProps
  concreteMaterial?: ConcreteMaterialProps
  crossSection?: CrossSectionProps
  reliabilityClass: 1 | 2 | 3
  setReliabilityClass: React.Dispatch<React.SetStateAction<1 | 2 | 3>>
  applyRc1Reduction: boolean
  setApplyRc1Reduction: React.Dispatch<React.SetStateAction<boolean>>
  imposedLoadCategory: ImposedLoadCategory
  setImposedLoadCategory: React.Dispatch<React.SetStateAction<ImposedLoadCategory>>
}

// NS-EN 1991-1-1 §6.3 categories; ψ values resolved backend-side from the codes DB (NA.A1.1).
const IMPOSED_LOAD_CATEGORIES: ImposedLoadCategory[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H']

const LoadConfigurationPanel: React.FC<LoadConfigurationPanelProps> = ({
  model,
  setModel,
  selectedSpan,
  setSelectedSpan,
  selectedLoadType,
  setSelectedLoadType,
  loadForm,
  setLoadForm,
  addLoadToSpan,
  removeLoad,
  editLoad,
  editingLoadId,
  setEditingLoadId,
  calculateSelfWeight,
  currentMaterial,
  prestressingMaterial,
  concreteMaterial,
  crossSection,
  reliabilityClass,
  setReliabilityClass,
  applyRc1Reduction,
  setApplyRc1Reduction,
  imposedLoadCategory,
  setImposedLoadCategory
}) =>
{
  const { t } = useTranslation()
  const selectedSpanData = useMemo(() => model.spans.find(span => span.id === selectedSpan), [model.spans, selectedSpan])
  // "All spans" shows every span's loads; a single span shows just its own.
  const loadsInSpan = useMemo(
    () => selectedSpan === ALL_SPANS_ID ? model.spans.flatMap(span => span.loads) : (selectedSpanData?.loads ?? []),
    [selectedSpan, selectedSpanData, model.spans]
  )

  const handleSpanChange = (event: ChangeEvent<HTMLSelectElement>) =>
  {
    const value = Number.parseInt(event.target.value, 10)
    setSelectedSpan(Number.isNaN(value) ? selectedSpan : value)
  }

  const handleLoadTypeChange = (event: ChangeEvent<HTMLSelectElement>) =>
  {
    setSelectedLoadType(event.target.value as LoadTypeOption)
  }

  const handleCategoryChange = (event: ChangeEvent<HTMLSelectElement>) =>
  {
    const category = event.target.value as LoadCategory
    setLoadForm(previous => ({
      ...previous,
      category
    }))
  }

  const handleEditLoad = useCallback((load: SMBeamLoad) =>
  {
    // Switch to the load's own span so the edit/save targets it (matters from the "all spans" view).
    setSelectedSpan(load.spanId)

    // Determine the load type option for the form
    const loadType: LoadTypeOption = load.type === 'AxialForce' ? 'AxialForce' : load.type as LoadTypeOption
    setSelectedLoadType(loadType)

    // Populate form with existing load values
    setLoadForm({
      pointLoad: load.pointLoad,
      pointLoadPosition: load.pointLoadPosition,
      normalForce: load.normalForce,
      normalForcePosition: load.normalForcePosition,
      normalForceEccentricity: load.normalForceEccentricity,
      uniformIntensity: load.uniformIntensity,
      startIntensity: load.startIntensity,
      endIntensity: load.endIntensity,
      startPosition: load.startPosition,
      endPosition: load.endPosition,
      axialForceEnd: load.axialForceEnd || 'right',
      axialForceValue: load.type === 'AxialForce' ? load.normalForce : 50,
      category: load.category || 'Variable'
    })

    setEditingLoadId(load.id)
  }, [setSelectedSpan, setSelectedLoadType, setLoadForm, setEditingLoadId])

  const handleCancelEdit = useCallback(() =>
  {
    setEditingLoadId(null)

    const spanData = model.spans.find(span => span.id === selectedSpan)
    const spanLength = spanData?.length ?? 5
    const newPosition = spanLength / 2

    setLoadForm({
      pointLoad: -10,
      pointLoadPosition: newPosition,
      normalForce: 0,
      normalForcePosition: newPosition,
      normalForceEccentricity: 0,
      uniformIntensity: -10,
      startIntensity: -20,
      endIntensity: -10,
      startPosition: 0,
      endPosition: spanLength,
      axialForceEnd: 'right',
      axialForceValue: 50,
      category: loadForm.category || 'Variable'
    })
  }, [setEditingLoadId, setLoadForm, model.spans, selectedSpan, loadForm.category])

  const handleSubmitLoad = useCallback(() =>
  {
    if (editingLoadId)
    {
      editLoad(editingLoadId, selectedSpan)
      setEditingLoadId(null)
    }
    else
    {
      addLoadToSpan()
    }
  }, [editingLoadId, editLoad, selectedSpan, setEditingLoadId, addLoadToSpan])

  return (
    <div>
      <div className="mb-3">
        <label className="form-label fw-bold">{t('beam.materials.reliabilityClass')}</label>
        <select
          className="form-select"
          value={reliabilityClass}
          onChange={(event) => setReliabilityClass(parseInt(event.target.value, 10) as 1 | 2 | 3)}
        >
          <option value="1">{t('beam.materials.reliabilityClass1')}</option>
          <option value="2">{t('beam.materials.reliabilityClass2')}</option>
          <option value="3">{t('beam.materials.reliabilityClass3')}</option>
        </select>
        {reliabilityClass === 1 && (
          <div className="form-check mt-2">
            <input
              className="form-check-input"
              type="checkbox"
              id="applyRc1Reduction"
              checked={applyRc1Reduction}
              onChange={(event) => setApplyRc1Reduction(event.target.checked)}
            />
            <label className="form-check-label" htmlFor="applyRc1Reduction">
              {t('beam.materials.applyRc1Reduction')}
            </label>
            <small className="text-muted d-block">{t('beam.materials.applyRc1ReductionHint')}</small>
          </div>
        )}
      </div>

      <div className="mb-3">
        <label className="form-label fw-bold">{t('beam.loads.imposedLoadCategory')}</label>
        <select
          className="form-select"
          value={imposedLoadCategory}
          onChange={(event) => setImposedLoadCategory(event.target.value as ImposedLoadCategory)}
        >
          {IMPOSED_LOAD_CATEGORIES.map(category => (
            <option key={category} value={category}>
              {t(`beam.loads.imposedLoadCategory${category}`)}
            </option>
          ))}
        </select>
        <small className="text-muted d-block">{t('beam.loads.imposedLoadCategoryHint')}</small>
      </div>

      <div className="mb-3">
        <label className="form-label fw-bold">{t('beam.loads.selectSpan')}</label>
        <select className="form-select" value={selectedSpan} onChange={handleSpanChange}>
          {model.spans.length > 1 && (
            <option value={ALL_SPANS_ID}>{t('beam.loads.allSpans')}</option>
          )}
          {model.spans.map(span => (
            <option key={span.id} value={span.id}>
              {t('beam.loads.spanOption', { id: span.id, length: span.length })}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-3">
        <label className="form-label fw-bold">{t('beam.loads.loadType')}</label>
        <select className="form-select" value={selectedLoadType} onChange={handleLoadTypeChange}>
          <option value="SelfWeight">{t('beam.loads.typeSelfWeight')}</option>
          <option value="UniformLoad">{t('beam.loads.typeUniform')}</option>
          <option value="PointLoad">{t('beam.loads.typePoint')}</option>
          <option value="TriangularRight">{t('beam.loads.typeTriRight')}</option>
          <option value="TriangularLeft">{t('beam.loads.typeTriLeft')}</option>
          <option value="AxialForce">{t('beam.loads.typeAxial')}</option>
        </select>
      </div>

      {selectedLoadType !== 'SelfWeight' && (
        <div className="mb-3">
          <label className="form-label fw-bold">{t('beam.loads.loadCategory')}</label>
          <select className="form-select" value={loadForm.category} onChange={handleCategoryChange}>
            <option value="Permanent">{t('beam.loads.catPermanent')}</option>
            <option value="Variable">{t('beam.loads.catVariable')}</option>
            <option value="Snow">{t('beam.loads.catSnow')}</option>
            <option value="Wind">{t('beam.loads.catWind')}</option>
          </select>
        </div>
      )}

      <LoadInputForm
        model={model}
        selectedLoadType={selectedLoadType}
        loadForm={loadForm}
        setLoadForm={setLoadForm}
        calculateSelfWeight={calculateSelfWeight}
        addLoadToSpan={handleSubmitLoad}
        editingLoadId={editingLoadId}
        onCancelEdit={handleCancelEdit}
      />

      <AppliedLoadsList
        selectedSpan={selectedSpan}
        loads={loadsInSpan}
        onRemoveLoad={removeLoad}
        onEditLoad={handleEditLoad}
        editingLoadId={editingLoadId}
      />

      {/* PT Cable Configuration (only shown for concrete) */}
      <PTCableConfigPanel
        model={model}
        setModel={setModel}
        currentMaterial={currentMaterial}
        prestressingMaterial={prestressingMaterial}
        concreteMaterial={concreteMaterial}
        crossSection={crossSection}
      />
    </div>
  )
}

export default LoadConfigurationPanel


